import { useCallback, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { hasPermission } from '@entities/access';
import { useAuth } from '@entities/auth';
import { ApiError, ForbiddenError } from '@shared/api/errors/errors';
import type { ProjectDto } from '@shared/api/contracts';
import { useProjects } from '@features/projects';
import { useAuditLogEvent } from '@features/audit-log';
import { useAuditLogFilters } from '@features/audit-log';
import { useAuditLogList } from '@features/audit-log';
import { formatAuditDateTime, shortId, type AuditLogFilters } from '@entities/audit-log';
import { Button, CabinetTablePageSkeleton, useToast } from '@shared/ui';
import { AuditEventDetail } from '@widgets/audit-log/detail';
import { AuditExportButton } from '@widgets/audit-log/export-button';
import { AuditLogFilters as AuditLogFiltersBar } from '@widgets/audit-log/filters';
import { AuditLogTable } from '@widgets/audit-log/table';

const hasExtraFilters = (filters: AuditLogFilters) =>
  Boolean(
    filters.category.length ||
      filters.eventType.length ||
      filters.outcome.length ||
      filters.actorType.length ||
      filters.actorUserId ||
      filters.subjectUserId ||
      filters.projectId ||
      filters.targetType ||
      filters.targetId ||
      filters.requestId
  );

export function AuditLogPage() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user, isLoading: isAuthLoading } = useAuth();
  const isAdmin = hasPermission(user, 'audit.read');
  const { projects } = useProjects();
  const projectList = useMemo(() => (projects ?? []) as ProjectDto[], [projects]);
  const { filters, eventId, errors, isValid, setFilters, openEvent, closeEvent, applyQuickFilter } =
    useAuditLogFilters();
  const list = useAuditLogList(filters, isAdmin && isValid);
  const known = list.items.find((item) => item.id === eventId) ?? null;
  const detail = useAuditLogEvent(isAdmin ? eventId : null, known);

  const onChange = useCallback((next: AuditLogFilters) => setFilters(next), [setFilters]);

  useEffect(() => {
    if (!detail.notFound || !eventId) return;
    toast(t('auditLog.errors.notFound'), 'error');
    closeEvent();
  }, [detail.notFound, eventId, toast, t, closeEvent]);

  const projectName = useCallback(
    (projectId: string) => projectList.find((project) => project.id === projectId)?.name ?? shortId(projectId),
    [projectList]
  );

  const apiError = useMemo(() => {
    if (!(list.error instanceof ApiError) || list.error instanceof ForbiddenError) return null;
    if (list.error.status === 400) return list.error.message;
    return null;
  }, [list.error]);

  if (isAuthLoading) {
    return <CabinetTablePageSkeleton showInfoBanner={false} statCount={0} tableRows={10} />;
  }
  if (!isAdmin) return <Navigate to="/balance" replace />;

  const forbidden = list.error instanceof ForbiddenError;
  const fatal = list.error && !apiError && !forbidden && list.items.length === 0;

  return (
    <div className="page-stack min-w-0">
      <div className="grid grid-cols-[auto_1fr] items-start gap-2.5 rounded-lg border border-border bg-accent/40 p-3 sm:gap-3 sm:p-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 text-sm">
          <p className="font-medium text-foreground">{t('auditLog.info.title')}</p>
          <p className="mt-0.5 text-muted-foreground">{t('auditLog.info.body')}</p>
        </div>
      </div>

      <AuditLogFiltersBar
        filters={filters}
        errors={errors}
        projects={projectList}
        apiError={apiError}
        periodText={
          list.period
            ? t('auditLog.periodShown', {
                from: formatAuditDateTime(list.period.from),
                to: formatAuditDateTime(list.period.to),
              })
            : null
        }
        isRefreshing={list.isValidating}
        onChange={onChange}
        onRefresh={() => void list.refresh()}
        exportSlot={
          <AuditExportButton
            filters={filters}
            period={list.period}
            disabled={!isValid}
            onExported={() => void list.refresh()}
          />
        }
      />

      {forbidden ? (
        <div className="surface-card py-12 text-center text-sm">{t('auditLog.errors.forbidden')}</div>
      ) : null}

      {fatal ? (
        <div className="surface-card space-y-3 py-12 text-center">
          <p className="text-sm text-muted-foreground">{t('auditLog.errors.unavailable')}</p>
          <Button type="button" variant="outline" onClick={() => void list.refresh()}>
            {t('auditLog.retry')}
          </Button>
        </div>
      ) : null}

      {!forbidden && !fatal && list.error && list.items.length > 0 ? (
        <div className="rounded-lg border border-border bg-pending px-3 py-2 text-sm text-pending-foreground">
          {t('auditLog.errors.stale')}
          <button type="button" className="ml-2 underline" onClick={() => void list.refresh()}>
            {t('auditLog.retry')}
          </button>
        </div>
      ) : null}

      {!forbidden && !fatal ? (
        <AuditLogTable
          items={list.items}
          isLoading={list.isLoading}
          isEmpty={!list.isLoading && list.items.length === 0}
          hasFilters={hasExtraFilters(filters)}
          hasMore={list.hasMore}
          isValidating={list.isValidating}
          projectName={projectName}
          onOpen={(item) => openEvent(item.id)}
          onActor={(userId) => applyQuickFilter({ actorUserId: String(userId) })}
          onLoadMore={list.loadMore}
        />
      ) : null}

      <AuditEventDetail
        event={detail.event}
        open={Boolean(eventId) && Boolean(detail.event)}
        projectName={projectName}
        onOpenChange={(next) => {
          if (!next) closeEvent();
        }}
        onQuickFilter={applyQuickFilter}
      />
    </div>
  );
}
